import React, { useContext, useEffect, useRef, useState } from 'react';
import { getTenantMachineTranslationResources } from '../../../../TranslationResources/services/api-calls';
import { TenantIdContext } from '../../../../../components/app/App';
import { StepEditor } from './StepEditor';
import Select, { SelectOption } from '@amzn/meridian/select';
import AmtLanguageSelect from '../../../../../global/language-select/AmtLanguageSelect';
import LanguageSelect from '../../../../../global/language-select/LanguageSelect';
import Toggle from '@amzn/meridian/toggle';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Link from '@amzn/meridian/link';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import Icon from '@amzn/meridian/icon';
import Button from '@amzn/meridian/button';

const MT_EDIT_HEADER = 'Edit Machine Translation';
const MT_MODELS_LABEL = 'Model';
const PREFERRED_SOURCE_LABEL = 'MT Source Locale';
const PREFERRED_TARGET_LABEL = 'MT Target Locale';
const PREFERRED_SOURCE_PLACEHOLDER = 'Select a source locale (optional)';
const PREFERRED_TARGET_PLACEHOLDER = 'Select a target locale (optional)';
const FORMALITY_LABEL = 'Formality';
const PROFANITY_LABEL = 'Profanity Masking';
const AMT_RESOURCE = 'AMT';

const PROFANITY_DEFAULT = true;
const FORMALITY_DEFAULT = 'DEFAULT';

export const MachineTranslationEditor = ({ subWorkflow, node, onAccept, onCancel }) => {
  const tenantId = useContext(TenantIdContext);
  const [mtResources, setMtResources] = useState([]);
  const [header, setHeader] = useState(MT_EDIT_HEADER);
  const [selectedMt, setSelectedMt] = useState(null);
  const [selectedEngine, setSelectedEngine] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [selectedModelVariant, setSelectedModelVariant] = useState(null);
  const [selectedRoleArn, setSelectedRoleArn] = useState(null);
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [isAmt, setIsAmt] = useState(false);
  const [preferredSourceLocale, setPreferredSourceLocale] = useState('');
  const [preferredTargetLocale, setPreferredTargetLocale] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [formality, setFormality] = useState(FORMALITY_DEFAULT);
  const [formalityHelpOpen, setFormalityHelpOpen] = useState(false);
  const [profanityFilter, setProfanityFilter] = useState(PROFANITY_DEFAULT);
  const [profanityHelpOpen, setProfanityHelpOpen] = useState(false);
  const formalityRef = useRef();
  const profanityRef = useRef();

  useEffect(() => {
    setHeader(`${MT_EDIT_HEADER} ${subWorkflow.sourceLocale} to ${subWorkflow.targetLocale}`);
    getTenantMachineTranslationResources({ tenantId: tenantId }).then(response => {
      setMtResources(
        response.data.map((entry, index) => {
          return {
            ...entry['resourceData'],
            resourceId: entry['resourceSk'],
          };
        })
      );
    });
  }, [node, subWorkflow, tenantId]);

  useEffect(() => {
    setSelectedEngine(node.parameters.type);
    setSelectedModel(node.parameters.modelName);
    setSelectedRoleArn(node.parameters.roleArn);
    if (node.parameters.modelEndpoint) {
      setSelectedEndpoint(node.parameters.modelEndpoint);
    }
    if (node.parameters.modelVariant) {
      setSelectedModelVariant(node.parameters.modelVariant);
    }
    setSelectedResourceId(node.parameters.resourceId);
    setIsAmt(node.parameters.type === AMT_RESOURCE);
    if (node.parameters.preferredSourceLocale) {
      setPreferredSourceLocale(node.parameters.preferredSourceLocale);
      setPreferredTargetLocale(node.parameters.preferredTargetLocale);
    }
    if (node.parameters.profanityFilter !== null && node.parameters.profanityFilter !== undefined) {
      setProfanityFilter(node.parameters.profanityFilter);
    }
    if (node.parameters.formality) {
      setFormality(node.parameters.formality);
    }
  }, [node]);

  useEffect(() => {
    if (mtResources === null || mtResources.length === 0) {
      return;
    }

    for (const [index, resource] of mtResources.entries()) {
      if (resource.resourceId === selectedResourceId) {
        setSelectedMt(index);
        return;
      }
    }
  }, [selectedResourceId, mtResources]);

  const handleAcceptClicked = () => {
    node.parameters.type = selectedEngine;
    node.parameters.modelName = selectedModel;
    node.parameters.roleArn = selectedRoleArn;
    if (selectedEndpoint) {
      node.parameters.modelEndpoint = selectedEndpoint;
    } else {
      delete node.parameters.modelEndpoint;
    }
    if (selectedModelVariant) {
      node.parameters.modelVariant = selectedModelVariant;
    } else {
      delete node.parameters.modelVariant;
    }
    node.parameters.resourceId = selectedResourceId;
    node.parameters.preferredSourceLocale = preferredSourceLocale;
    node.parameters.preferredTargetLocale = preferredTargetLocale;
    if (isAmt) {
      node.parameters.profanityFilter = profanityFilter;
      node.parameters.formality = formality;
    }
    onAccept();
  };

  useEffect(() => {
    let valid = true;
    if (
      (preferredSourceLocale !== '' && preferredTargetLocale === '') ||
      (preferredSourceLocale === '' && preferredTargetLocale !== '') ||
      (preferredSourceLocale !== '' && preferredSourceLocale === preferredTargetLocale)
    ) {
      valid = false;
    }
    setIsValid(valid);
  }, [selectedEngine, preferredSourceLocale, preferredTargetLocale]);

  const updateSelections = value => {
    const selectedResource = mtResources[value];
    setSelectedResourceId(selectedResource.resourceId);
    setSelectedEngine(selectedResource.engine);
    setSelectedModel(selectedResource.model);
    setSelectedEndpoint(selectedResource.endpoint);
    setSelectedModelVariant(selectedResource.modelVariant);
    setSelectedRoleArn(selectedResource.roleArn);
    const newAmt = selectedResource.engine === AMT_RESOURCE;
    setIsAmt(newAmt);
    if (isAmt !== newAmt) {
      setPreferredSourceLocale('');
      setPreferredTargetLocale('');
      setProfanityFilter(PROFANITY_DEFAULT);
      setFormality(FORMALITY_DEFAULT);
    }
  };

  return (
    <StepEditor
      modalHeader={header}
      onAccept={handleAcceptClicked}
      onCancel={onCancel}
      initialRoleArn={node.parameters.roleArn}
      isValid={isValid}
    >
      <div>
        <div>
          <Text>{MT_MODELS_LABEL}</Text>
          <Select value={selectedMt} onChange={updateSelections} placeholder="Select a machine translation model">
            {mtResources &&
              mtResources.map((resource, resourceIndex) => {
                let label = resource.engine;
                if (resource.model) {
                  label = `${label}: ${resource.model}`;
                }
                if (resource.modelVariant) {
                  label = `${label}-${resource.modelVariant}`;
                }
                return <SelectOption value={resourceIndex} key={resourceIndex} label={label} />;
              })}
          </Select>
        </div>
        <div>
          <div className="mt-3">
            <Text>{PREFERRED_SOURCE_LABEL}</Text>
            {isAmt ? (
              <AmtLanguageSelect
                value={preferredSourceLocale}
                onChange={setPreferredSourceLocale}
                placeholderText={PREFERRED_SOURCE_PLACEHOLDER}
              />
            ) : (
              <LanguageSelect
                value={preferredSourceLocale}
                isDefaultOptionSelectable
                onChange={setPreferredSourceLocale}
              />
            )}
          </div>
          <div className="mt-3">
            <Text>{PREFERRED_TARGET_LABEL}</Text>
            {isAmt ? (
              <AmtLanguageSelect
                value={preferredTargetLocale}
                onChange={setPreferredTargetLocale}
                placeholderText={PREFERRED_TARGET_PLACEHOLDER}
              />
            ) : (
              <LanguageSelect
                value={preferredTargetLocale}
                isDefaultOptionSelectable
                onChange={setPreferredTargetLocale}
              />
            )}
          </div>
          {isAmt ? (
            <div className="mt-3">
              <Text>
                {FORMALITY_LABEL}
                <Button type="icon" onClick={() => setFormalityHelpOpen(true)} ref={formalityRef}>
                  <Icon tokens={infoKnockoutTokens} />
                </Button>
                <Popover
                  anchorNode={formalityRef.current}
                  open={formalityHelpOpen}
                  onClose={() => setFormalityHelpOpen(false)}
                  position="top"
                >
                  <PopoverHeader closeLabel="Close">AMT Formality Control</PopoverHeader>
                  <Column>
                    <Text>
                      Amazon Translate supports the formality setting for a subset of target languages. If your target
                      language is not supported, then the formality filter will not apply.
                    </Text>
                    <Link
                      href="https://docs.aws.amazon.com/translate/latest/dg/customizing-translations-formality.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      See the AMT documentation for more details.
                    </Link>
                  </Column>
                </Popover>
              </Text>
              <ButtonGroup value={formality} onChange={setFormality}>
                <ButtonOption value="DEFAULT">Default</ButtonOption>
                <ButtonOption value="FORMAL">Formal</ButtonOption>
                <ButtonOption value="INFORMAL">Informal</ButtonOption>
              </ButtonGroup>
            </div>
          ) : null}
          {isAmt ? (
            <div className="mt-3">
              <Toggle checked={profanityFilter} onChange={setProfanityFilter}>
                {PROFANITY_LABEL}
                <Button type="icon" onClick={() => setProfanityHelpOpen(true)} ref={profanityRef}>
                  <Icon tokens={infoKnockoutTokens} />
                </Button>
                <Popover
                  anchorNode={profanityRef.current}
                  open={profanityHelpOpen}
                  onClose={() => setProfanityHelpOpen(false)}
                  position="top"
                >
                  <PopoverHeader closeLabel="Close">AMT Profanity Masking</PopoverHeader>
                  <Column>
                    <Text>Amazon Translate has a few languages that do not yet include profanity masking.</Text>
                    <Link
                      href="https://docs.aws.amazon.com/translate/latest/dg/customizing-translations-profanity.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      See the AMT documentation for more details.
                    </Link>
                  </Column>
                </Popover>
              </Toggle>
            </div>
          ) : null}
        </div>
      </div>
    </StepEditor>
  );
};
